<template>
    <div class="qrs-root" :class="{ 'qrs-fullscreen': fullscreen }">
        <!-- 视频流 -->
        <video ref="video" class="qrs-video" playsinline autoplay muted></video>
        <canvas ref="canvas" class="qrs-canvas"></canvas>

        <!-- 遮罩 -->
        <div v-if="showMask" class="qrs-mask">
            <div class="qrs-mask-hole"></div>
        </div>

        <!-- 扫描框 -->
        <div v-if="showFrame" class="qrs-frame-wrapper">
            <div class="qrs-frame" :class="{ 'qrs-success': isSuccessFlash }">
                <div class="qrs-corner qrs-corner-tl"></div>
                <div class="qrs-corner qrs-corner-tr"></div>
                <div class="qrs-corner qrs-corner-bl"></div>
                <div class="qrs-corner qrs-corner-br"></div>
                <div class="qrs-scan-line"></div>
            </div>
        </div>

        <!-- 顶部栏 -->
        <div v-if="showTopBar" class="qrs-top">
            <div class="qrs-top-inner">
                <div class="qrs-title-wrap">
                    <div class="qrs-logo"><van-icon name="scan" /></div>
                    <div>
                        <h1 class="qrs-title">{{ title }}</h1>
                        <p class="qrs-status">{{ statusText }}</p>
                    </div>
                </div>
                <button v-if="closable" class="qrs-circle-btn" @click="handleClose" aria-label="关闭">
                    <van-icon name="cross" />
                </button>
            </div>
        </div>

        <!-- 扫描提示 -->
        <div v-if="showHint && !loadingVisible" class="qrs-hint">
            <div class="qrs-hint-pill">
                <van-icon name="aim" style="margin-right:4px" />{{ hint }}
            </div>
        </div>

        <!-- 缩放倍率提示（捏合缩放 / 双击复位时短暂显示） -->
        <div v-if="zoomTipVisible" class="qrs-zoom-tip">
            <van-icon name="eye-o" style="margin-right:4px" />{{ zoomText }}
        </div>

        <!-- 历史按钮 -->
        <button v-if="enableHistory && !loadingVisible" class="qrs-history-btn" @click="openHistory" aria-label="历史记录">
            <van-icon name="clock-o" />
        </button>

        <!-- 底部工具栏（showToolbar 或 torchAlways 时显示；torchAlways 仅展示手电筒按钮） -->
        <div v-if="!loadingVisible && (showToolbar || torchAlways)" class="qrs-bottom" :class="{ 'qrs-bottom--torch-only': torchAlways && !showToolbar }">
            <div class="qrs-bottom-inner">
                <button v-if="showToolbar && enableAlbum" class="qrs-tool" @click="pickAlbum">
                    <div class="qrs-tool-icon"><van-icon name="photo-o" /></div>
                    <span class="qrs-tool-label">相册</span>
                </button>
                <button v-if="enableTorch" class="qrs-tool" @click="handleTorch">
                    <div
                        class="qrs-tool-icon"
                        :class="{
                            'qrs-torch-active': torchOn,
                            'qrs-torch-disabled': !torchSupported
                        }"
                    >
                        <van-icon :name="torchOn ? 'fire' : 'fire-o'" />
                    </div>
                    <span class="qrs-tool-label">手电筒</span>
                </button>
                <button v-if="showToolbar && enableSwitch && cameras.length >= 2" class="qrs-tool" @click="handleSwitch" @contextmenu.prevent="openCameraPicker">
                    <div class="qrs-tool-icon"><van-icon name="exchange" /></div>
                    <span class="qrs-tool-label">切换</span>
                </button>
                <button v-if="showToolbar && enableCameraPicker" class="qrs-tool" @click="openCameraPicker">
                    <div class="qrs-tool-icon">
                        <van-icon name="photograph" />
                        <span v-if="cameras.length >= 2" class="qrs-tool-badge">{{ cameras.length }}</span>
                    </div>
                    <span class="qrs-tool-label">镜头</span>
                </button>
            </div>
        </div>

        <!-- 隐藏的文件输入 -->
        <input
            ref="fileInput"
            type="file"
            accept="image/*"
            style="display:none"
            @change="onFilePicked"
        />

        <!-- 加载层：已注释，组件挂载后直接进入扫码界面 -->
        <!-- <transition name="qrs-fade">
            <div v-if="loadingVisible" class="qrs-loading">
                <div class="qrs-loading-inner">
                    <div class="qrs-loading-logo"><van-icon name="scan" /></div>
                    <h2 class="qrs-loading-title">{{ title }}</h2>
                    <p class="qrs-loading-desc">{{ loadingText }}</p>
                    <button class="qrs-btn-primary" @click="startCamera">
                        <van-icon name="photograph" style="margin-right:4px" />开启相机
                    </button>
                    <button v-if="enableAlbum" class="qrs-btn-ghost" @click="pickAlbum">
                        <van-icon name="photo-o" style="margin-right:4px" />或从相册选择图片
                    </button>
                    <p class="qrs-loading-tip">推荐在 HTTPS 环境下使用 · 需相机权限</p>
                </div>
            </div>
        </transition> -->

        <!-- 结果弹窗 -->
        <div v-if="resultModalVisible && parsedResult" class="qrs-modal">
            <div class="qrs-modal-card">
                <div class="qrs-modal-header">
                    <div class="qrs-modal-header-title">
                        <van-icon name="checked" size="22" />
                        <span>识别成功</span>
                    </div>
                    <p class="qrs-modal-header-sub">类型：{{ parsedResult.typeLabel }}</p>
                </div>
                <div class="qrs-modal-body">
                    <div class="qrs-result-box">{{ parsedResult.display || parsedResult.raw }}</div>
                    <div class="qrs-btn-row">
                        <button class="qrs-btn-copy" @click="copyContent">
                            <van-icon name="description" style="margin-right:4px" />复制
                        </button>
                        <button class="qrs-btn-action" @click="performAction">
                            <van-icon :name="parsedResult.actionIcon" style="margin-right:4px" />{{ parsedResult.actionLabel }}
                        </button>
                    </div>
                    <button class="qrs-btn-continue" @click="continueScan">继续扫描</button>
                </div>
            </div>
        </div>

        <!-- 摄像头选择弹层 -->
        <div v-if="cameraPickerVisible" class="qrs-picker-mask" @click.self="cameraPickerVisible = false">
            <div class="qrs-picker-sheet">
                <div class="qrs-picker-head">
                    <h3>选择摄像头</h3>
                    <button class="qrs-circle-btn" @click="cameraPickerVisible = false">
                        <van-icon name="cross" />
                    </button>
                </div>
                <div class="qrs-picker-body">
                    <div v-if="!cameras.length" class="qrs-picker-empty">
                        <van-icon name="warning-o" />
                        <p>未检测到可用摄像头<br />（iOS Safari 通常仅暴露 1 个）</p>
                    </div>
                    <button
                        v-for="(cam, idx) in cameras"
                        :key="cam.deviceId || idx"
                        class="qrs-picker-item"
                        :class="{ 'qrs-picker-item-active': cam.deviceId === currentDeviceId }"
                        @click="selectCamera(cam)"
                    >
                        <div class="qrs-picker-item-icon">
                            <van-icon :name="cam.facingMode === 'user' ? 'manager-o' : 'photograph'" />
                        </div>
                        <div class="qrs-picker-item-main">
                            <p class="qrs-picker-item-title">{{ cam.label }}</p>
                            <p class="qrs-picker-item-sub">
                                {{ cam.facingMode === 'user' ? '前置' : (cam.facingMode === 'environment' ? '后置' : '未知方向') }}
                                <span v-if="cam.deviceId === currentDeviceId" class="qrs-picker-tag">使用中</span>
                            </p>
                        </div>
                        <van-icon v-if="cam.deviceId === currentDeviceId" name="checked" class="qrs-picker-item-check" />
                    </button>
                </div>
            </div>
        </div>

        <!-- Toast -->
        <transition name="qrs-fade">
            <div v-if="toast.visible" class="qrs-toast" :class="'qrs-toast-' + toast.type">
                <van-icon :name="toastIconClass" style="margin-right:4px" />{{ toast.msg }}
            </div>
        </transition>

        <!-- 历史记录 -->
        <div v-if="historyVisible" class="qrs-history-panel" @click.self="closeHistory">
            <div class="qrs-history-drawer">
                <div class="qrs-history-header">
                    <h3>扫描历史</h3>
                    <button class="qrs-circle-btn" @click="closeHistory">
                        <van-icon name="cross" />
                    </button>
                </div>
                <div class="qrs-history-list">
                    <div v-if="!historyItems.length" class="qrs-history-empty">
                        <van-icon name="records" />
                        <p style="font-size:13px;margin:0">暂无扫描历史</p>
                    </div>
                    <div
                        v-for="(item, idx) in historyItems"
                        :key="idx"
                        class="qrs-history-item"
                        @click="replayHistory(item)"
                    >
                        <div class="qrs-history-item-inner">
                            <van-icon
                                class="qrs-history-item-icon"
                                :name="item.source === 'album' ? 'photo-o' : 'photograph'"
                            />
                            <div style="flex:1;min-width:0">
                                <p class="qrs-history-item-text">{{ previewText(item.content) }}</p>
                                <p class="qrs-history-item-time">{{ formatTime(item.timestamp) }}</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="qrs-history-footer">
                    <button class="qrs-btn-danger" @click="clearHistory">
                        <van-icon name="delete-o" style="margin-right:4px" />清空历史
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ScannerEngine } from './scanner-engine.js';
import { parseContent } from './parser.js';
import { HistoryStore } from './history.js';
import './QrScanner.css';

/** 单指双击复位：两次轻触之间的最大时间间隔（ms） */
const DOUBLE_TAP_WINDOW = 300;
/** 单指双击复位：两次轻触之间的最大位移（px），超出即视为两次独立的单击 */
const DOUBLE_TAP_TOLERANCE = 30;

export default {
    name: 'QrScanner',
    props: {
        title: { type: String, default: '极速扫码' },
        hint: { type: String, default: '支持二维码 / 条形码 · 对准扫描框即可识别' },
        autoStart: { type: Boolean, default: false },
        fullscreen: { type: Boolean, default: true },
        showTopBar: { type: Boolean, default: true },
        showToolbar: { type: Boolean, default: true },
        // 当 showToolbar 为 false 时，是否仍单独显示手电筒按钮（悬浮右下角）
        torchAlways: { type: Boolean, default: false },
        showMask: { type: Boolean, default: true },
        showFrame: { type: Boolean, default: true },
        showHint: { type: Boolean, default: true },
        showResultModal: { type: Boolean, default: true },
        enableAlbum: { type: Boolean, default: true },
        enableTorch: { type: Boolean, default: true },
        enableSwitch: { type: Boolean, default: true },
        enableHistory: { type: Boolean, default: true },
        enableCameraPicker: { type: Boolean, default: true },
        closable: { type: Boolean, default: false },
        historyKey: { type: String, default: 'qr_scan_history_v1' },
        historyMax: { type: Number, default: 50 },
        /** 重复过滤时间（ms） */
        duplicateInterval: { type: Number, default: 3000 },
        /** 是否允许双指捏合缩放（数字变焦） */
        enableZoom: { type: Boolean, default: true },
        /** 变焦倍率下限 / 上限 */
        minZoom: { type: Number, default: 1 },
        maxZoom: { type: Number, default: 4 }
    },
    data() {
        return {
            scanner: null,
            history: null,
            statusText: '正在初始化相机…',
            loadingVisible: false,
            loadingText: '支持二维码与条形码（EAN/UPC/Code128 等）',
            torchOn: false,
            torchSupported: false,
            resultModalVisible: false,
            parsedResult: null,
            lastResultContent: '',
            lastResultTime: 0,
            isSuccessFlash: false,
            isScanning: false,
            historyVisible: false,
            historyItems: [],
            cameras: [],
            currentDeviceId: '',
            cameraPickerVisible: false,
            _switchPressTimer: null,
            toast: { visible: false, msg: '', type: 'info' },
            _toastTimer: null,

            // === 双指捏合缩放（数字变焦）===
            zoom: 1,
            zoomTipVisible: false,
            _pinching: false,
            _pinchStartDist: 0,
            _pinchStartZoom: 1,
            _zoomTipTimer: null,
            _transformTimer: null,
            _gestureBlocker: null,
            // 上一次有效轻触的时间戳与坐标，仅用于判定双击复位
            _lastTapAt: 0,
            _lastTapX: 0,
            _lastTapY: 0
        };
    },
    computed: {
        /** 缩放倍率展示文案 */
        zoomText() {
            return this.zoom.toFixed(1) + 'x';
        },
        toastIconClass() {
            return {
                info: 'info-o',
                success: 'passed',
                error: 'warning-o'
            }[this.toast.type] || 'info-o';
        }
    },
    mounted() {
        this.history = new HistoryStore(this.historyKey, this.historyMax);
        this.scanner = new ScannerEngine({
            video: this.$refs.video,
            canvas: this.$refs.canvas,
            onResult: this.handleScanResult,
            onStatus: (msg) => { this.statusText = msg; },
            onError: (err) => this.showToast(err, 'error')
        });

        // 让引擎的倍率范围与组件 props 保持一致
        this.scanner.minZoom = this.minZoom;
        this.scanner.maxZoom = this.maxZoom;

        document.addEventListener('visibilitychange', this.onVisibility);
        this.bindZoomGestures();

        // 组件挂载后始终自动开启相机，跳过加载层
        this.startCamera();
    },
    beforeDestroy() {
        document.removeEventListener('visibilitychange', this.onVisibility);
        this.unbindZoomGestures();
        if (this.scanner) this.scanner.stop();
        clearTimeout(this._toastTimer);
        clearTimeout(this._zoomTipTimer);
        clearTimeout(this._transformTimer);
    },
    methods: {
        async startCamera() {
            this.loadingText = '正在请求相机权限…';
            try {
                await this.scanner.start();
                this.isScanning = true;
                this.loadingVisible = false;
                this.statusText = '正在扫描…将二维码对准框内';
                this.torchOn = this.scanner.torchOn;
                this.torchSupported = this.scanner.torchSupported;
                this.syncZoomState();
                this.$emit('ready');
                this.refreshCameras();
                // iOS Safari 首次拿到权限后，enumerateDevices 的 label 有延迟，500ms 再刷一次
                setTimeout(() => this.refreshCameras(), 600);
                setTimeout(() => this.refreshCameras(), 1500);
            } catch (err) {
                console.error(err);
                this.loadingText = err.message || '相机启动失败，请检查权限或使用相册扫码';
                this.$emit('error', err);
            }
        },
        handleScanResult(result) {
            const now = Date.now();
            if (result.data === this.lastResultContent && now - this.lastResultTime < this.duplicateInterval) {
                return;
            }
            this.lastResultContent = result.data;
            this.lastResultTime = now;

            if (navigator.vibrate) navigator.vibrate([30, 20, 30]);

            this.isSuccessFlash = true;
            setTimeout(() => { this.isSuccessFlash = false; }, 600);

            this.scanner.pause();
            this.isScanning = false;

            this.history.add({
                content: result.data,
                format: result.format || '',
                source: result.source || 'camera',
                timestamp: now
            });

            const parsed = parseContent(result.data, result.format);
            this.parsedResult = parsed;

            // 向外抛事件
            this.$emit('decode', {
                text: result.data,
                format: result.format,
                source: result.source || 'camera',
                parsed
            });

            if (this.showResultModal) {
                this.resultModalVisible = true;
            } else {
                // 不展示内置弹窗时，短暂 pause 后自动继续
                setTimeout(() => this.continueScan(), 500);
            }
        },
        continueScan() {
            this.resultModalVisible = false;
            if (this.scanner && this.scanner.stream) {
                this.scanner.resume();
                this.isScanning = true;
            }
        },
        pickAlbum() {
            this.$refs.fileInput.click();
        },
        async onFilePicked(e) {
            const file = e.target.files && e.target.files[0];
            e.target.value = '';
            if (!file) return;
            this.showToast('正在解析图片…', 'info');
            try {
                const r = await this.scanner.decodeFromImage(file);
                if (r) {
                    this.handleScanResult({ data: r.text, format: r.format, source: 'album' });
                } else {
                    this.showToast('未能识别到二维码/条形码，请换张清晰的图片重试', 'error');
                }
            } catch (err) {
                this.showToast('图片解析失败：' + err.message, 'error');
            }
        },
        async handleTorch() {
            try {
                const on = await this.scanner.toggleTorch();
                this.torchOn = on;
                this.torchSupported = this.scanner.torchSupported;
            } catch (err) {
                this.showToast(err.message || '当前设备不支持手电筒', 'error');
            }
        },
        async handleSwitch() {
            this.statusText = '切换相机中…';
            try {
                await this.scanner.switchCamera();
                this.statusText = '正在扫描…';
                this.torchOn = this.scanner.torchOn;
                this.torchSupported = this.scanner.torchSupported;
                this.currentDeviceId = this.scanner.currentDeviceId;
                this.syncZoomState();
                this.refreshCameras();
            } catch {
                this.showToast('切换相机失败', 'error');
            }
        },
        async refreshCameras() {
            if (!this.scanner) return;
            try {
                const list = await this.scanner.listCameras(true);
                this.cameras = list || [];
                this.currentDeviceId = this.scanner.currentDeviceId || '';
                // 调试信息（方便排查手机枚举情况）
                if (typeof window !== 'undefined' && window.__qrs_debug__) {
                    console.log('[QrScanner] cameras:', JSON.parse(JSON.stringify(this.cameras)), 'current:', this.currentDeviceId);
                }
            } catch {}
        },
        async openCameraPicker() {
            await this.refreshCameras();
            this.cameraPickerVisible = true;
        },
        async selectCamera(cam) {
            if (!cam || !cam.deviceId || cam.deviceId === this.currentDeviceId) {
                this.cameraPickerVisible = false;
                return;
            }
            this.cameraPickerVisible = false;
            this.statusText = `切换到 ${cam.label}…`;
            try {
                await this.scanner.openByDeviceId(cam.deviceId);
                this.statusText = '正在扫描…';
                this.torchOn = this.scanner.torchOn;
                this.torchSupported = this.scanner.torchSupported;
                this.currentDeviceId = this.scanner.currentDeviceId;
                this.syncZoomState();
                this.showToast(`已切换到：${cam.label}`, 'success');
            } catch (err) {
                this.showToast(err.message || '切换摄像头失败', 'error');
            }
        },
        async copyContent() {
            const content = (this.parsedResult && (this.parsedResult.raw || this.parsedResult.display)) || '';
            try {
                await navigator.clipboard.writeText(content);
                this.showToast('已复制到剪贴板', 'success');
            } catch {
                const ta = document.createElement('textarea');
                ta.value = content;
                document.body.appendChild(ta);
                ta.select();
                try { document.execCommand('copy'); } catch {}
                document.body.removeChild(ta);
                this.showToast('已复制到剪贴板', 'success');
            }
        },
        performAction() {
            const action = this.parsedResult && this.parsedResult.action;
            if (!action) return;
            switch (action.type) {
                case 'url': window.open(action.value, '_blank'); break;
                case 'tel': window.location.href = 'tel:' + action.value; break;
                case 'sms': window.location.href = 'sms:' + action.value; break;
                case 'email': window.location.href = 'mailto:' + action.value; break;
                case 'wifi':
                    if (navigator.clipboard) {
                        navigator.clipboard.writeText(action.value).then(() => this.showToast('Wi-Fi 密码已复制', 'success'));
                    }
                    break;
                default:
                    if (navigator.clipboard) {
                        navigator.clipboard.writeText(action.value).then(() => this.showToast('已复制', 'success'));
                    }
            }
        },
        openHistory() {
            this.historyItems = this.history.getAll();
            this.historyVisible = true;
        },
        closeHistory() { this.historyVisible = false; },
        replayHistory(item) {
            const parsed = parseContent(item.content, item.format);
            this.historyVisible = false;
            if (this.scanner) this.scanner.pause();
            this.isScanning = false;
            this.parsedResult = parsed;
            if (this.showResultModal) this.resultModalVisible = true;
            this.$emit('history-click', { item, parsed });
        },
        clearHistory() {
            if (!window.confirm('确定清空所有历史记录？')) return;
            this.history.clear();
            this.historyItems = [];
            this.showToast('历史已清空', 'success');
        },
        /* ================= 双指捏合缩放（数字变焦） ================= */
        /** 绑定触摸手势（touchmove 必须 non-passive，否则无法阻止浏览器默认缩放） */
        bindZoomGestures() {
            if (!this.enableZoom || !this.$el) return;
            // 惰性创建并保存引用，保证 removeEventListener 能正确解绑
            if (!this._gestureBlocker) {
                this._gestureBlocker = (e) => { if (e.cancelable) e.preventDefault(); };
            }
            const el = this.$el;
            el.addEventListener('touchstart', this.onZoomTouchStart, { passive: true });
            el.addEventListener('touchmove', this.onZoomTouchMove, { passive: false });
            el.addEventListener('touchend', this.onZoomTouchEnd, { passive: true });
            el.addEventListener('touchcancel', this.onZoomTouchEnd, { passive: true });
            // iOS Safari 会把双指手势当作页面缩放，这里一并拦截
            el.addEventListener('gesturestart', this._gestureBlocker, { passive: false });
            el.addEventListener('gesturechange', this._gestureBlocker, { passive: false });
        },
        unbindZoomGestures() {
            const el = this.$el;
            if (!el) return;
            el.removeEventListener('touchstart', this.onZoomTouchStart);
            el.removeEventListener('touchmove', this.onZoomTouchMove);
            el.removeEventListener('touchend', this.onZoomTouchEnd);
            el.removeEventListener('touchcancel', this.onZoomTouchEnd);
            if (this._gestureBlocker) {
                el.removeEventListener('gesturestart', this._gestureBlocker);
                el.removeEventListener('gesturechange', this._gestureBlocker);
            }
        },
        /** 两指间距 */
        _touchDistance(a, b) {
            const dx = a.clientX - b.clientX;
            const dy = a.clientY - b.clientY;
            return Math.sqrt(dx * dx + dy * dy);
        },
        /** 清空双击手势记录（时间戳 + 坐标） */
        _clearTapRecord() {
            this._lastTapAt = 0;
            this._lastTapX = 0;
            this._lastTapY = 0;
        },
        onZoomTouchStart(e) {
            if (!this.enableZoom) return;
            // 双指：开始捏合
            if (e.touches.length === 2) {
                this._pinching = true;
                this._pinchStartDist = this._touchDistance(e.touches[0], e.touches[1]);
                this._pinchStartZoom = this.zoom;
                // 捏合与双击互斥，清掉上一次轻触记录，避免捏合结束后紧接着的单指轻触被误判成双击
                this._clearTapRecord();
                this.showZoomTip();
                return;
            }
            // 单指双击复位（避开按钮与各类浮层上的点击）
            // 非单指或未放大时无需复位，同样要清空记录，避免遗留的时间戳参与后续判定
            if (e.touches.length !== 1 || this.zoom <= this.minZoom) {
                this._clearTapRecord();
                return;
            }
            const t = e.target;
            // 落在按钮 / 浮层上的点击不参与双击判定，且必须清空已有记录：
            // 否则「空白处点一下 → 点按钮 → 再点空白处」会在时间窗内被串成一次双击复位
            if (t && t.closest && t.closest('button, .qrs-picker-sheet, .qrs-history-drawer, .qrs-modal-card, .qrs-toast')) {
                this._clearTapRecord();
                return;
            }
            const touch = e.touches[0];
            const now = Date.now();
            // 双击需同时满足「时间够近」与「位置够近」，避免屏幕不同位置的快速连点被误判为双击
            const isDoubleTap =
                now - this._lastTapAt < DOUBLE_TAP_WINDOW &&
                this._touchDistance(touch, { clientX: this._lastTapX, clientY: this._lastTapY }) <= DOUBLE_TAP_TOLERANCE;
            if (isDoubleTap) {
                this._clearTapRecord();
                this.resetZoom(true);
            } else {
                this._lastTapAt = now;
                this._lastTapX = touch.clientX;
                this._lastTapY = touch.clientY;
            }
        },
        onZoomTouchMove(e) {
            if (!this._pinching || e.touches.length !== 2) return;
            if (e.cancelable) e.preventDefault();
            const d = this._touchDistance(e.touches[0], e.touches[1]);
            if (!this._pinchStartDist || !d) return;
            this.applyZoom(this._pinchStartZoom * (d / this._pinchStartDist));
            this.showZoomTip();
        },
        onZoomTouchEnd(e) {
            if (this._pinching && e.touches.length < 2) {
                this._pinching = false;
                this._pinchStartDist = 0;
            }
        },
        /** 应用倍率：同步引擎采样区域 + video 视觉缩放 */
        applyZoom(value, { animate = false, tip = false } = {}) {
            const next = this.scanner ? this.scanner.setZoom(value) : value;
            this.zoom = next;
            this.applyVideoTransform(animate);
            if (tip) this.showZoomTip();
        },
        /**
         * 缩放 video 元素
         * 注意：前置摄像头依赖 .qrs-mirrored 的 scaleX(-1) 做镜像，
         * 内联 transform 会覆盖该样式，因此必须把镜像一起组合进来
         */
        applyVideoTransform(animate = false) {
            const v = this.$refs.video;
            if (!v) return;
            const mirrored = v.classList.contains('qrs-mirrored');
            clearTimeout(this._transformTimer);
            v.style.transformOrigin = 'center center';
            v.style.transition = animate ? 'transform .18s ease-out' : 'none';
            v.style.transform = mirrored ? `scaleX(-1) scale(${this.zoom})` : `scale(${this.zoom})`;
            if (animate) {
                this._transformTimer = setTimeout(() => {
                    if (this.$refs.video) this.$refs.video.style.transition = 'none';
                }, 220);
            }
        },
        /** 相机重开/切换后引擎已把倍率重置为 1，这里同步视觉与倍率 */
        syncZoomState() {
            this.$nextTick(() => {
                this.zoom = this.scanner ? this.scanner.getZoom() : this.minZoom;
                this.applyVideoTransform(false);
            });
        },
        resetZoom(animate = true) {
            if (this.zoom <= this.minZoom) return;
            this.applyZoom(this.minZoom, { animate, tip: true });
        },
        showZoomTip() {
            clearTimeout(this._zoomTipTimer);
            this.zoomTipVisible = true;
            this._zoomTipTimer = setTimeout(() => { this.zoomTipVisible = false; }, 1200);
        },
        showToast(msg, type = 'info') {
            clearTimeout(this._toastTimer);
            this.toast = { visible: true, msg, type };
            this._toastTimer = setTimeout(() => { this.toast.visible = false; }, 2200);
        },
        previewText(text) {
            return text && text.length > 60 ? text.slice(0, 60) + '…' : text;
        },
        formatTime(ts) {
            const d = new Date(ts);
            const now = new Date();
            const diff = now - d;
            if (diff < 60000) return '刚刚';
            if (diff < 3600000) return Math.floor(diff / 60000) + ' 分钟前';
            if (diff < 86400000) return Math.floor(diff / 3600000) + ' 小时前';
            return (d.getMonth() + 1) + '/' + d.getDate() + ' ' +
                String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
        },
        onVisibility() {
            if (!this.scanner) return;
            if (document.hidden) {
                this.scanner.pause();
            } else if (this.isScanning) {
                this.scanner.resume();
            }
        },
        handleClose() {
            if (this.scanner) this.scanner.stop();
            this.$emit('close');
        },
        // ======= 对外暴露的命令式 API =======
        start() { return this.startCamera(); },
        stop() { if (this.scanner) this.scanner.stop(); },
        pause() { if (this.scanner) this.scanner.pause(); },
        resume() { this.continueScan(); },
        decodeImage(file) { return this.scanner.decodeFromImage(file); },
        getHistory() { return this.history ? this.history.getAll() : []; },
        /** 设置缩放倍率（自动夹取到 minZoom ~ maxZoom） */
        setZoom(value) { this.applyZoom(value); return this.zoom; },
        /** 读取当前缩放倍率 */
        getZoom() { return this.zoom; }
    }
};
</script>